import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { RotateCcw, Send, X, Timer, Loader2, Star } from 'lucide-react';
import { checkEnglishWord } from '../../../utils/englishDictionary';
import { useTranslation } from '../../../i18n';

// Natural tile metrics at scale=1
const BASE_TILE_SIZE = 56;
const BASE_GAP = 6;
const TILES_PADDING = 16;
const ACTION_BAR_HEIGHT = 44;
const STATUS_BAR_HEIGHT = 40;
const CURRENT_WORD_HEIGHT = 36;

const DEFAULT_GRID_SIZE = 4;

const normalizeWord = (w) => (w || '').toUpperCase().replace(/[^A-ZА-ЯІЇЄҐ]/gi, '');

// Returns an array of letters (length = size*size) padded with '?' for empty cells
const splitLetters = (letters, size) => {
  const total = size * size;
  const padded = (letters || '').padEnd(total, '?').slice(0, total);
  return padded.split('');
};

const BoggleElement = ({
  element,
  isEditor = false,
  studentView = false,
  currentState = null,
  showResult = false,
  isCorrect = null,
  onStateChange,
  onCheck,
  onRetry,
  disabled = false,
  scale = 1,
  hideCheckButton = false
}) => {
  const { t } = useTranslation();
  const { content, style } = element;
  const {
    mode = 'targets',
    rows = DEFAULT_GRID_SIZE,
    cols = DEFAULT_GRID_SIZE,
    letters = '',
    targetWords = [],
    timeLimit = 60,
    minWordsToPass = 1,
    allowRetry = false,
    tileColor = '#3d6b3d',
    actionBarColor = '#f9fafb',
    fontFamily,
    fontSize: customFontSize,
    textColor = '#ffffff',
  } = content || {};
  const isTimeMode = mode === 'time';

  const backgroundColor = style?.backgroundColor || '#ffffff';
  const isActionBarTransparent = actionBarColor === 'transparent';

  const size = Math.max(rows || DEFAULT_GRID_SIZE, cols || DEFAULT_GRID_SIZE);
  const cellLetters = useMemo(() => splitLetters(letters, size), [letters, size]);
  const normTargets = useMemo(
    () => (targetWords || []).map(normalizeWord).filter(Boolean),
    [targetWords]
  );

  // Resize observer to scale tiles inside the bounding box
  const containerRef = useRef(null);
  const [boxScale, setBoxScale] = useState(scale);

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const update = (w, h) => {
      if (w <= 0 || h <= 0) return;
      const naturalGridWidth = size * BASE_TILE_SIZE + (size - 1) * BASE_GAP + TILES_PADDING * 2;
      const naturalHeight =
        size * BASE_TILE_SIZE +
        (size - 1) * BASE_GAP +
        TILES_PADDING * 2 +
        STATUS_BAR_HEIGHT +
        CURRENT_WORD_HEIGHT +
        ACTION_BAR_HEIGHT;
      const next = Math.min(w / naturalGridWidth, h / naturalHeight) * scale;
      setBoxScale(Math.max(0.3, next));
    };
    update(node.clientWidth, node.clientHeight);
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) update(e.contentRect.width, e.contentRect.height);
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, [scale, size]);

  const tileSize = Math.max(20, Math.round(BASE_TILE_SIZE * boxScale));
  const tileFontSize = Math.max(10, Math.round((customFontSize || 22) * boxScale));
  const tileGap = Math.max(2, Math.round(BASE_GAP * boxScale));
  const tileRadius = Math.max(4, Math.round(10 * boxScale));

  // Local play state: { path, foundWords, startedAt }
  const [path, setPath] = useState(currentState?.path || []);
  const [foundWords, setFoundWords] = useState(currentState?.foundWords || []);
  const [startedAt, setStartedAt] = useState(currentState?.startedAt || 0);
  const [timerRemaining, setTimerRemaining] = useState(timeLimit);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Refs mirror the latest state so rapid successive taps don't read a stale
  // `path` / `foundWords` from a not-yet-rerendered closure and clobber each
  // other. Without this, quickly tapping N-A-M-E can collapse to just "NE".
  const pathRef = useRef(path);
  const foundWordsRef = useRef(foundWords);
  const startedAtRef = useRef(startedAt);

  useEffect(() => {
    if (!currentState) return;
    const nextPath = currentState.path || [];
    const nextFound = currentState.foundWords || [];
    setPath(nextPath);
    setFoundWords(nextFound);
    pathRef.current = nextPath;
    foundWordsRef.current = nextFound;
    // Don't overwrite a running timer just because the parent didn't echo
    // `startedAt` back — many parent handlers only persist `path`/`foundWords`.
    if (typeof currentState.startedAt === 'number' && currentState.startedAt > 0) {
      setStartedAt(currentState.startedAt);
      startedAtRef.current = currentState.startedAt;
    }
  }, [currentState]);

  // Countdown tick for time mode
  useEffect(() => {
    if (isEditor || !isTimeMode || !startedAt || showResult) return;
    const tick = () => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      setTimerRemaining(Math.max(0, timeLimit - elapsed));
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [isEditor, isTimeMode, startedAt, showResult, timeLimit]);

  // Auto-finalize when timer hits zero
  const expiredRef = useRef(false);
  useEffect(() => {
    expiredRef.current = false;
  }, [startedAt]);
  useEffect(() => {
    if (isEditor || !isTimeMode || !startedAt || showResult) return;
    if (timerRemaining > 0) return;
    if (expiredRef.current) return;
    expiredRef.current = true;
    onCheck?.({ foundWords, totalRequired: normTargets.length, mode: 'time' });
  }, [isEditor, isTimeMode, startedAt, showResult, timerRemaining, onCheck, foundWords, normTargets.length]);

  const cellRowCol = (idx) => ({ row: Math.floor(idx / size), col: idx % size });
  const isAdjacent = (a, b) => {
    if (a === b) return false;
    const A = cellRowCol(a);
    const B = cellRowCol(b);
    return Math.abs(A.row - B.row) <= 1 && Math.abs(A.col - B.col) <= 1;
  };

  const emit = (next) => {
    onStateChange?.(next);
  };

  // Start timer on first cell click in time mode
  const ensureStarted = () => {
    if (!isTimeMode || startedAtRef.current) return startedAtRef.current;
    const ts = Date.now();
    startedAtRef.current = ts;
    setStartedAt(ts);
    return ts;
  };

  const handleCellClick = (idx) => {
    if (disabled || showResult || isEditor) return;
    const ch = cellLetters[idx];
    if (!ch || ch === '?') return;
    setErrorMsg('');
    const started = ensureStarted();
    const currentPath = pathRef.current;
    const currentFound = foundWordsRef.current;

    let next;
    if (currentPath.length === 0) {
      next = [idx];
    } else {
      const last = currentPath[currentPath.length - 1];
      if (idx === last) {
        next = currentPath.slice(0, -1);
      } else if (currentPath.includes(idx) || !isAdjacent(last, idx)) {
        next = [idx];
      } else {
        next = [...currentPath, idx];
      }
    }
    pathRef.current = next;
    setPath(next);
    emit({ path: next, foundWords: currentFound, startedAt: started });
  };

  const currentWord = path.map((idx) => cellLetters[idx] || '').join('').toUpperCase();

  // Targets mode (wordsearch): auto-count a target the moment the current
  // selection spells it. Boggle (time mode) still needs an explicit tap because
  // its words are validated against a dictionary asynchronously.
  useEffect(() => {
    if (isEditor || showResult || disabled || isTimeMode) return;
    if (!currentWord) return;
    if (!normTargets.includes(currentWord)) return;
    if (foundWords.includes(currentWord)) return;
    const nextFound = [...foundWords, currentWord];
    foundWordsRef.current = nextFound;
    pathRef.current = [];
    setFoundWords(nextFound);
    setPath([]);
    setErrorMsg('');
    emit({ path: [], foundWords: nextFound, startedAt });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWord, isTimeMode, isEditor, showResult, disabled, normTargets, foundWords, startedAt]);

  const handleSubmitWord = async () => {
    if (disabled || showResult || isEditor) return;
    if (!currentWord || currentWord.length < 2) {
      setErrorMsg(t('games.wordPool.minLetters', { count: 2 }));
      return;
    }
    if (foundWords.includes(currentWord)) {
      setErrorMsg(t('games.wordPool.alreadyFound'));
      return;
    }

    if (isTimeMode) {
      // Time mode has no teacher-supplied list; validate against an English
      // dictionary and reject proper nouns (names, cities, countries, etc.).
      setSubmitting(true);
      const ok = await checkEnglishWord(currentWord);
      setSubmitting(false);
      if (!ok) {
        setErrorMsg(t('games.wordPool.notInDictionary'));
        return;
      }
    }
    // Targets mode: teacher curates the target list, so any adjacent sequence
    // is accepted (target words count toward completion, others are bonus).

    const nextFound = [...foundWords, currentWord];
    foundWordsRef.current = nextFound;
    pathRef.current = [];
    setFoundWords(nextFound);
    setPath([]);
    setErrorMsg('');
    emit({ path: [], foundWords: nextFound, startedAt });
  };

  const handleClearPath = () => {
    if (disabled || showResult || isEditor) return;
    pathRef.current = [];
    setPath([]);
    setErrorMsg('');
    emit({ path: [], foundWords: foundWordsRef.current, startedAt });
  };

  const handleCheck = () => {
    onCheck?.({ foundWords, totalRequired: normTargets.length, mode });
  };

  const handleRetry = () => {
    pathRef.current = [];
    foundWordsRef.current = [];
    startedAtRef.current = 0;
    setPath([]);
    setFoundWords([]);
    setStartedAt(0);
    setTimerRemaining(timeLimit);
    setErrorMsg('');
    setSubmitting(false);
    onRetry?.();
  };

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  // Editor preview - static grid, non-interactive
  if (isEditor) {
    return (
      <div
        ref={containerRef}
        className="w-full h-full flex flex-col rounded-xl border-2 border-gray-200 overflow-hidden shadow-sm"
        style={{ backgroundColor }}
      >
        <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 flex items-center gap-2 text-xs text-gray-600 overflow-hidden">
          {isTimeMode ? (
            <>
              <Timer size={12} className="text-indigo-500 flex-shrink-0" />
              <span className="font-medium text-gray-700 flex-shrink-0">
                {t('games.wordPool.timeLimit', { time: formatTime(timeLimit) })}
              </span>
              {minWordsToPass > 0 && (
                <span className="text-gray-500 truncate">
                  · {t('games.wordPool.minWords', { count: minWordsToPass })} {minWordsToPass === 1 ? t('games.wordPool.wordSingular') : t('games.wordPool.wordPlural')}
                </span>
              )}
            </>
          ) : (
            <>
              <span className="font-medium text-gray-700 flex-shrink-0">
                {t('games.wordPool.findWordsCount', { count: normTargets.length })}
              </span>
              <div className="flex flex-wrap gap-1 min-w-0">
                {normTargets.slice(0, 6).map((w) => (
                  <span key={w} className="px-2 py-0.5 rounded-full bg-white border border-gray-200">
                    {w}
                  </span>
                ))}
                {normTargets.length > 6 && <span>…</span>}
              </div>
            </>
          )}
        </div>

        <div className="flex-1 min-h-0 p-2 flex items-center justify-center overflow-hidden">
          {cellLetters.length > 0 ? (
            <div
              className="grid"
              style={{
                gridTemplateColumns: `repeat(${size}, ${tileSize}px)`,
                gap: `${tileGap}px`
              }}
            >
              {cellLetters.map((ch, i) => (
                <div
                  key={i}
                  className="flex items-center justify-center font-bold"
                  style={{
                    width: `${tileSize}px`,
                    height: `${tileSize}px`,
                    fontSize: `${tileFontSize}px`,
                    borderRadius: `${tileRadius}px`,
                    backgroundColor: tileColor,
                    color: textColor,
                    opacity: ch === '?' ? 0.3 : 1,
                    ...(fontFamily ? { fontFamily } : {}),
                  }}
                >
                  {ch === '?' ? '' : ch}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">{t('games.wordPool.fillLettersInSettings')}</p>
          )}
        </div>
      </div>
    );
  }

  // Interactive / Classroom mode
  const hideChrome = true;
  const foundTargets = normTargets.filter((w) => foundWords.includes(w));
  const bonusWords = foundWords.filter((w) => !normTargets.includes(w));

  // Wordsearch scoring: 1 star per valid found word, capped at 5.
  const validFoundCount = isTimeMode ? foundWords.length : foundTargets.length;
  const maxStars = Math.min(5, isTimeMode ? 5 : Math.max(1, normTargets.length));
  const earnedStars = Math.min(validFoundCount, maxStars);

  return (
    <div
      ref={containerRef}
      className={`w-full h-full flex flex-col rounded-xl overflow-hidden ${
        hideChrome ? '' : 'border-2 border-transparent shadow-sm'
      }`}
    >
      {/* Status bar - different per mode */}
      {isTimeMode ? (
        <div className="px-3 py-1.5 border-b border-gray-100 bg-gray-50 flex items-center gap-2 text-xs overflow-hidden">
          <Timer
            size={14}
            className={timerRemaining <= 10 ? 'text-red-500' : 'text-indigo-500'}
          />
          <span
            className={`font-mono font-bold flex-shrink-0 ${
              timerRemaining <= 10 ? 'text-red-600' : 'text-gray-800'
            }`}
          >
            {formatTime(startedAt ? timerRemaining : timeLimit)}
          </span>
          <span className="text-gray-700 font-medium flex-shrink-0">
            · {t('games.wordPool.found', { count: foundWords.length })}
          </span>
          {minWordsToPass > 0 && (
            <span className="text-gray-500 flex-shrink-0">{t('games.wordPool.minWords', { count: minWordsToPass })}</span>
          )}
          <div className="flex flex-wrap gap-1 min-w-0 ml-2">
            {foundWords.map((w) => (
              <span
                key={w}
                className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium"
              >
                {w}
              </span>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* Targets list */}
          <div className="px-3 py-1.5 border-b border-gray-100 bg-gray-50 flex items-center gap-2 text-xs overflow-hidden">
            <span className="text-gray-700 font-medium flex-shrink-0">
              {t('games.wordPool.targets', { done: foundTargets.length, total: normTargets.length })}
            </span>
            <div className="flex flex-wrap gap-1 min-w-0">
              {normTargets.map((w) => {
                const found = foundWords.includes(w);
                return (
                  <span
                    key={w}
                    className={`px-2 py-0.5 rounded-full font-medium ${
                      found ? 'bg-green-100 text-green-700' : 'bg-white border border-gray-200 text-gray-400'
                    }`}
                  >
                    {found ? w : '·'.repeat(w.length)}
                  </span>
                );
              })}
            </div>
          </div>

          {/* Bonus words found (not in target list) */}
          {bonusWords.length > 0 && (
            <div className="px-3 py-1 border-b border-gray-100 bg-amber-50 flex items-center gap-2 text-xs overflow-hidden">
              <span className="text-amber-700 font-medium flex-shrink-0">
                {t('games.wordPool.bonus', { count: bonusWords.length })}
              </span>
              <div className="flex flex-wrap gap-1 min-w-0">
                {bonusWords.map((w) => (
                  <span
                    key={w}
                    className="px-2 py-0.5 rounded-full bg-white text-amber-700 border border-amber-200 font-medium"
                  >
                    {w}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Grid */}
      <div className="flex-1 min-h-0 p-2 flex items-center justify-center overflow-hidden">
        {cellLetters.length > 0 ? (
          <div
            className="grid"
            style={{
              gridTemplateColumns: `repeat(${size}, ${tileSize}px)`,
              gap: `${tileGap}px`
            }}
          >
            {cellLetters.map((ch, i) => {
              const pathIdx = path.indexOf(i);
              const inPath = pathIdx >= 0;
              const isLast = inPath && pathIdx === path.length - 1;
              const isEmpty = !ch || ch === '?';
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => handleCellClick(i)}
                  disabled={disabled || showResult || isEmpty}
                  className={`
                    flex items-center justify-center font-bold select-none
                    transition-all duration-150
                    ${!disabled && !showResult && !isEmpty ? 'cursor-pointer hover:scale-105' : 'cursor-default'}
                    ${inPath ? 'ring-4 ring-yellow-300 ring-offset-1' : ''}
                    ${isLast ? 'scale-110' : ''}
                  `}
                  style={{
                    width: `${tileSize}px`,
                    height: `${tileSize}px`,
                    fontSize: `${tileFontSize}px`,
                    borderRadius: `${tileRadius}px`,
                    backgroundColor: tileColor,
                    color: textColor,
                    opacity: isEmpty ? 0.25 : 1,
                    ...(fontFamily ? { fontFamily } : {}),
                  }}
                >
                  {isEmpty ? '' : ch}
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-400">{t('games.wordPool.lettersNotFilled')}</p>
        )}
      </div>

      {/* Current selection bar */}
      {!showResult && (
        <div className="px-3 py-1.5 border-t border-gray-100 bg-gray-50 flex items-center gap-2 text-sm">
          <span className="text-gray-500 text-xs flex-shrink-0">{t('games.wordPool.wordLabel')}</span>
          <span className="font-bold tracking-wider text-gray-800 flex-1 truncate">
            {currentWord || '—'}
          </span>
          {errorMsg && (
            <span className="text-xs text-red-600 font-medium flex-shrink-0">{errorMsg}</span>
          )}
          {path.length > 0 && (
            <button
              onClick={handleClearPath}
              disabled={submitting}
              className="p-1 hover:bg-gray-200 rounded transition disabled:opacity-50"
              title={t('games.wordPool.clear')}
            >
              <X size={14} className="text-gray-500" />
            </button>
          )}
          {isTimeMode && (
            <button
              onClick={handleSubmitWord}
              disabled={path.length < 2 || submitting}
              className="flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-lg transition disabled:cursor-not-allowed"
              style={
                path.length >= 2 && !submitting
                  ? { backgroundColor: '#2A4D25', color: '#fff' }
                  : { backgroundColor: '#e5e7eb', color: '#9ca3af' }
              }
              onMouseEnter={(e) => {
                if (path.length >= 2 && !submitting) {
                  e.currentTarget.style.backgroundColor = '#1e3a1a';
                }
              }}
              onMouseLeave={(e) => {
                if (path.length >= 2 && !submitting) {
                  e.currentTarget.style.backgroundColor = '#2A4D25';
                }
              }}
            >
              {submitting ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              {t('games.wordPool.addLetters')}
            </button>
          )}
        </div>
      )}

      {/* Action bar */}
      <div
        className={`px-3 py-2 flex items-center justify-end gap-2 ${
          hideChrome || isActionBarTransparent ? '' : 'border-t border-gray-100'
        }`}
        style={{ backgroundColor: hideChrome ? 'transparent' : actionBarColor }}
      >
        {showResult && !isCorrect && allowRetry && (
          <button
            onClick={handleRetry}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition"
          >
            <RotateCcw size={14} />
            {t('games.common.tryAgain')}
          </button>
        )}

        {!showResult && !hideCheckButton && (
          <button
            onClick={handleCheck}
            disabled={!isTimeMode && normTargets.length === 0}
            className={`px-4 py-1.5 text-sm font-medium rounded-lg transition ${
              isTimeMode || normTargets.length > 0
                ? 'bg-indigo-500 text-white hover:bg-indigo-600'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isTimeMode ? t('games.common.finish') : t('games.common.check')}
          </button>
        )}

        {showResult && (
          <div className="flex items-center gap-1 mr-1">
            {Array.from({ length: maxStars }).map((_, i) => (
              <Star
                key={i}
                size={16}
                className={i < earnedStars ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300 fill-gray-200'}
              />
            ))}
            <span className="ml-1 text-xs font-semibold text-gray-700">{earnedStars}/{maxStars}</span>
          </div>
        )}

      </div>
    </div>
  );
};

export default BoggleElement;
